/**
 * Сортировка списка задач подразделения по столбцам.
 *
 * Список приходит целиком, без страниц, поэтому сортируется здесь, а не
 * на сервере. Пустые значения («—») всегда внизу, в какую сторону ни
 * сортируй: иначе при обратном порядке строки без срока или проекта
 * вытесняли бы наверх то, ради чего сортировали.
 */

import type { SortState } from "@/shared/ui";

import type { TaskListItem } from "@taskflow/client-core/tasks";

export const SORT_FIELDS = [
  "key",
  "board",
  "project",
  "status",
  "priority",
  "assignees",
  "due",
] as const;
export type SortField = (typeof SORT_FIELDS)[number];

export function isSortField(value: string | null): value is SortField {
  return (SORT_FIELDS as readonly string[]).includes(value ?? "");
}

const collator = new Intl.Collator("ru", { numeric: true, sensitivity: "base" });

/** Номер задачи — с учётом чисел: OA-2 раньше OA-10. */
function byKey(a: TaskListItem, b: TaskListItem): number {
  return collator.compare(a.key, b.key);
}

function assigneeLabel(task: TaskListItem): string | null {
  const first = task.assignees[0];
  return first === undefined ? null : `${first.lastName} ${first.firstName}`;
}

/**
 * Значение столбца для сравнения; null — пустая ячейка. Приоритет — по
 * порядку справочника, а не по алфавиту: «Высокий» раньше «Низкого»
 * по буквам, но не по смыслу.
 */
function valueOf(
  task: TaskListItem,
  field: SortField,
  priorityRank: ReadonlyMap<string, number>,
): string | number | null {
  switch (field) {
    case "key":
      return task.key;
    case "board":
      return task.boardName ?? null;
    case "project":
      return task.projectName ?? null;
    case "status":
      return task.statusLabel ?? null;
    case "priority":
      return task.priorityName == null ? null : (priorityRank.get(task.priorityName) ?? null);
    case "assignees":
      return assigneeLabel(task);
    case "due":
      return task.dueDate ?? null;
  }
}

/**
 * Без выбранного столбца — прежний порядок: ближайший срок сверху,
 * задачи без срока в конце. Равные значения упорядочены по номеру задачи.
 */
export function sortDepartmentTasks(
  tasks: readonly TaskListItem[],
  sort: (SortState & { field: SortField }) | null,
  priorityRank: ReadonlyMap<string, number>,
): TaskListItem[] {
  const field: SortField = sort?.field ?? "due";
  const sign = sort?.direction === "desc" ? -1 : 1;

  return [...tasks].sort((a, b) => {
    const left = valueOf(a, field, priorityRank);
    const right = valueOf(b, field, priorityRank);
    if (left === null || right === null) {
      if (left === right) return byKey(a, b);
      return left === null ? 1 : -1;
    }
    const order =
      typeof left === "number" && typeof right === "number"
        ? left - right
        : collator.compare(String(left), String(right));
    return order === 0 ? byKey(a, b) : sign * order;
  });
}
